'use client';

import { Text, Flex, Box } from '@radix-ui/themes';
import { TLSiteCartData } from '@trashlab/api/src/domains/consumer/types/index';
import { ShopStepFrame } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shop/shop-step-frame';
import { IconSuccessfullOrder } from '@trashlab/sitesapp/src/organizations/fasttrac/template/components/icons/icons';
import { useNavigate } from '@tanstack/react-router';
import { trpcConsumerGetOrderNumberByCart } from '@trashlab/api/src/generated/hooks';
import { LeadTrackingInjector } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shop/lead-tracking-injector';
import { IconTruck, IconUser } from '@tabler/icons-react';

export const OrderConfirmationStep = ({
  cartData,
}: {
  cartData: TLSiteCartData;
}) => {
  const navigate = useNavigate();

  // Poll for order number - order is created asynchronously after payment
  const { data: orderData } = trpcConsumerGetOrderNumberByCart.useQuery(
    {
      cartId: cartData.cart.id,
    },
    {
      // @ts-expect-error - refetchInterval function works but types don't match
      refetchInterval: (data: { orderNumber?: string } | undefined) => {
        return data?.orderNumber ? false : 1000;
      },
      refetchIntervalInBackground: true,
    },
  );

  const orderNumber = orderData?.orderNumber;

  // Build URLs for tracking and portal
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const orderTrackingUrl = orderNumber ? `${baseUrl}/order/${orderNumber}` : null;
  const customerPortalUrl = `${baseUrl}/customer-portal`;

  return (
    <>
      {/* Inject lead tracking data into localStorage, cookies, and DOM for analytics tools */}
      <LeadTrackingInjector orderNumber={orderNumber} />
      <ShopStepFrame
      cartId={cartData.cart.id}
      onNext={() => navigate({ to: '/' })}
      canContinue={true}
      isLoading={false}
      hideBackButton
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        className="my-10"
      >
        <IconSuccessfullOrder />

        <Text size="4" weight="bold">
          Order request submitted successfully!
        </Text>
        <Text size="3" align="center" className="max-w-md">
          Your order has been placed successfully! Thank you for choosing{' '}
          {cartData.cart.organization.name}. You will receive a confirmation
          email shortly with all the details. If you have any questions or need
          further assistance, feel free to contact us.
        </Text>

        {/* Quick Links Section */}
        <Box className="mt-6 w-full max-w-md">
          <Flex direction="column" gap="3">
            {orderTrackingUrl && (
              <a
                href={orderTrackingUrl}
                className="flex items-center gap-3 p-4 bg-[var(--accent-2)] hover:bg-[var(--accent-3)] rounded-lg transition-colors"
              >
                <Box className="p-2 rounded-full bg-[var(--primary-color)] text-white">
                  <IconTruck size={20} />
                </Box>
                <Flex direction="column">
                  <Text size="2" weight="bold">Track Your Order</Text>
                  <Text size="1" color="gray">View delivery status and updates</Text>
                </Flex>
              </a>
            )}
            <a
              href={customerPortalUrl}
              className="flex items-center gap-3 p-4 bg-[var(--accent-2)] hover:bg-[var(--accent-3)] rounded-lg transition-colors"
            >
              <Box className="p-2 rounded-full bg-[var(--primary-color)] text-white">
                <IconUser size={20} />
              </Box>
              <Flex direction="column">
                <Text size="2" weight="bold">Customer Portal</Text>
                <Text size="1" color="gray">Manage your orders and account</Text>
              </Flex>
            </a>
          </Flex>
        </Box>
      </Flex>
      </ShopStepFrame>
    </>
  );
};
